"use client";

import type { Voice as CMSVoice, ICmsLink } from "@mw/shared/payload-types";

import { useCallback } from "react";
import { VoiceCard, type VoiceCardVariant } from "@ui/components/composers/VoicesCarousel";
import { Disc } from "@ui/components/core/Disc/Disc";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { ImageSizes } from "@ui/lib/image";
import { AudioProvider, useAudio } from "@/app/_providers/AudioProvider";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { getLinkProps } from "@/app/_utilities/payload";
import { appUrls } from "@/constants";
import { useLoadingFeatureFlagEnabled } from "@/app/_analytics/posthog-client";
import { FEATURE_FLAGS } from "@/app/_analytics/feature-flags";

import { Block } from "../Block";

import imgVoiceBg01 from "./images/Voice BG 01.png";
import imgVoiceBg02 from "./images/Voice BG 02.png";
import imgVoiceBg03 from "./images/Voice BG 03.png";
import imgVoiceBg04 from "./images/Voice BG 04.png";
import imgVoiceBg05 from "./images/Voice BG 05.png";
import imgVoiceBg06 from "./images/Voice BG 06.png";
import imgVoiceBg07 from "./images/Voice BG 07.png";
import imgVoiceBg08 from "./images/Voice BG 08.png";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";

type Voice = Omit<CMSVoice, "id" | "isVoiceLibraryVoice" | "updatedAt" | "createdAt">;

interface BlockVoicesSliderProps {
  voices: Voice[];
  disableNoise?: boolean;
  showLegend: boolean;
  cardVariant: VoiceCardVariant;
  bottomCta?: ICmsLink | null;
  bottomCtaLabel?: string;
  bottomCtaHref?: string;
  cardCtaLabel?: string;
  previewLanguage?: string;
}

const VOICE_IMAGES = [
  imgVoiceBg01,
  imgVoiceBg02,
  imgVoiceBg03,
  imgVoiceBg04,
  imgVoiceBg05,
  imgVoiceBg06,
  imgVoiceBg07,
  imgVoiceBg08,
];

const TRACKING_SOURCE = "voices_slider";

const BlockVoicesSliderLayout = ({
  voices,
  disableNoise = false,
  showLegend = true,
  cardVariant,
  bottomCta,
  bottomCtaLabel,
  bottomCtaHref,
  cardCtaLabel,
  previewLanguage,
}: BlockVoicesSliderProps) => {
  const { play, playingId } = useAudio();
  const bottomCtaLinkProps = getLinkProps(bottomCta);
  const { enabled: noAutoScroll } = useLoadingFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_VOICES_SLIDER_NO_AUTOSCROLL, {
    variantKey: "test",
  });

  const handleCtaClick = useCallback((voiceId: string) => {
    captureEvent(EventName.mw_voices_slider_cta_clicked, { voice_id: voiceId }, { transport: "sendBeacon" });
  }, []);

  const getPreviewUrl = (voice: Voice): string | undefined => {
    if (previewLanguage && voice.languages) {
      const entry = voice.languages.find(l => l.language === previewLanguage);
      if (entry?.previewUrl) return entry.previewUrl;
    }
    return voice.previewUrl ?? undefined;
  };

  if (voices.length === 0) {
    return null;
  }

  const handleToggle = (voice: Voice, index: number) => {
    const previewUrl = getPreviewUrl(voice);
    if (previewUrl) {
      play(voice.voiceId, previewUrl, false, { source: TRACKING_SOURCE, voice_id: voice.voiceId, position: index });
    }
  };

  return (
    <Block name='voices-slider'>
      <ScrollGrid
        items={voices}
        columns={4}
        legend={{
          show: showLegend,
          getItemTitle: voice => voice.category || voice.name,
          textClassName: "tw-type-base",
          className: "tw-mt-[50px]",
        }}
        renderItem={(voice, index) => {
          const image = VOICE_IMAGES[index % VOICE_IMAGES.length];
          return (
            <VoiceCard
              variant={cardVariant}
              title={voice.category || voice.name}
              description={voice.shortDescription ?? undefined}
              ctaLabel={cardCtaLabel}
              ctaHref={voice.link ?? appUrls.voiceLibrary}
              onCtaClick={() => handleCtaClick(voice.voiceId)}
              media={
                <Disc
                  image={{
                    src: image,
                    alt: voice.name,
                    blur: 5,
                    sizes: ImageSizes.FULL_1_4_ON_LG_HALF_ON_MD,
                  }}
                  noiseOpacity={disableNoise ? 0 : 0.5}
                  showCenterContent={!!getPreviewUrl(voice)}
                  isPlaying={playingId === voice.voiceId}
                  onPlayPauseClick={() => handleToggle(voice, index)}
                />
              }
            />
          );
        }}
      />

      {bottomCtaLinkProps ? (
        <div className='tw-flex tw-justify-center tw-mt-4'>
          <LinkTracked
            asButton
            buttonVariants={{ variant: "elevated", size: "lg" }}
            href={bottomCtaLinkProps.href}
            target={bottomCtaLinkProps.target}
            rel={bottomCtaLinkProps.rel}
            aria-label={bottomCtaLinkProps["aria-label"]}
            eventName={EventName.mw_tts_voice_library_explore_clicked}
            eventProperties={{ source_component: "voices_slider" }}
          >
            {bottomCtaLinkProps.children}
          </LinkTracked>
        </div>
      ) : bottomCtaLabel ? (
        <div className='tw-flex tw-justify-center tw-mt-4'>
          <LinkTracked
            asButton
            buttonVariants={{ variant: "elevated", size: "lg" }}
            href={bottomCtaHref ?? appUrls.voiceLibrary}
            eventName={EventName.mw_tts_voice_library_explore_clicked}
            eventProperties={{ source_component: "voices_slider" }}
          >
            {bottomCtaLabel}
          </LinkTracked>
        </div>
      ) : null}
    </Block>
  );
};

export const BlockVoicesSlider = (props: BlockVoicesSliderProps) => {
  return (
    <AudioProvider>
      <BlockVoicesSliderLayout {...props} />
    </AudioProvider>
  );
};

BlockVoicesSlider.displayName = "BlockVoicesSlider";
